<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealKanbanBoard from 'dashboard/components-next/Deals/DealKanbanBoard.vue';

const { t } = useI18n();
const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();

const kanbanData = computed(() => dealsStore.getKanbanData);
const uiFlags = computed(() => dealsStore.uiFlags);
const pipelines = computed(() => pipelinesStore.getRecords);
const selectedPipelineId = ref(null);

const handleMoveStage = async (dealId, stageId) => {
  try {
    await dealsStore.moveStage(dealId, stageId);
  } catch (error) {
    useAlert(error.message || t('DEALS.ERROR_MESSAGE'));
    // Reload kanban to revert on error
    if (selectedPipelineId.value) {
      dealsStore.fetchKanban(selectedPipelineId.value);
    }
  }
};

const onPipelineChange = async () => {
  if (selectedPipelineId.value) {
    await dealsStore.fetchKanban(selectedPipelineId.value);
  }
};

onMounted(async () => {
  await pipelinesStore.get();
  const defaultPipeline =
    pipelines.value.find(p => p.isDefault) || pipelines.value[0];
  if (defaultPipeline) {
    selectedPipelineId.value = defaultPipeline.id;
    await dealsStore.fetchKanban(defaultPipeline.id);
  }
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="flex items-center justify-between mx-auto max-w-5xl py-4">
        <div class="flex items-center gap-4">
          <h2 class="text-xl font-medium truncate text-n-slate-12">
            {{ t('DEALS.KANBAN.TITLE') }}
          </h2>
          <select
            v-model="selectedPipelineId"
            class="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
            @change="onPipelineChange"
          >
            <option v-for="p in pipelines" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-auto px-6 py-4">
      <Spinner v-if="uiFlags.fetchingKanban" />
      <DealKanbanBoard
        v-else
        :columns="kanbanData"
        :is-moving="uiFlags.movingStage"
        @move-stage="handleMoveStage"
      />
    </main>
  </div>
</template>
